// ── WhatsAppBroadcastsPage ─────────────────────────────────────────
//
// Lists WhatsApp broadcast campaigns.
// From here users can:
//   • View all broadcasts with status + progress
//   • Create a new draft (opens wizard drawer)
//   • Click into a broadcast → detail page

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  BarChart2,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  Megaphone,
  PauseCircle,
  PlayCircle,
  PlusCircle,
  Search,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Floating } from '@/components/ui/Floating';
import { toast } from '@/components/ui/toast';
import api from '@/lib/api';
import {
  listWhatsappAccounts,
  listBroadcasts,
  createBroadcast,
  listTemplates,
  type WhatsappAccountListItem,
  type BroadcastRow,
  type BroadcastStatus,
  type WhatsappTemplate,
} from '../api/whatsapp.api';
import { formatDate } from '@/lib/dateFormat';
import { SelectField } from '@/components/ui/SelectField';

// ── Status helpers ─────────────────────────────────────────────────

const STATUS_BADGE: Record<BroadcastStatus, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  SCHEDULED: 'bg-blue-50 text-blue-700',
  RUNNING: 'bg-emerald-50 text-emerald-700',
  PAUSED: 'bg-amber-50 text-amber-700',
  COMPLETED: 'bg-indigo-50 text-indigo-700',
  CANCELED: 'bg-slate-100 text-slate-400',
  FAILED: 'bg-red-50 text-red-700',
};

const STATUS_ICONS: Record<BroadcastStatus, React.ReactNode> = {
  DRAFT: <Clock className="h-3 w-3" />,
  SCHEDULED: <Clock className="h-3 w-3" />,
  RUNNING: <PlayCircle className="h-3 w-3" />,
  PAUSED: <PauseCircle className="h-3 w-3" />,
  COMPLETED: <BarChart2 className="h-3 w-3" />,
  CANCELED: <XCircle className="h-3 w-3" />,
  FAILED: <AlertCircle className="h-3 w-3" />,
};

function progressPct(b: BroadcastRow): number {
  if (!b.totalRecipients) return 0;
  return Math.round(((b.sentCount + b.failedCount) / b.totalRecipients) * 100);
}

// ── Lead status multi-select (real pipeline statuses, no free-text) ──

interface LeadStatus {
  id: string;
  name: string;
  color?: string | null;
  isActive: boolean;
  skipInBroadcasts?: boolean;
}

/**
 * A checkbox dropdown of the tenant's actual pipeline statuses — replaces the
 * old comma-separated free-text box so users pick from what exists instead of
 * guessing names. Values are status NAMES (that's what the audienceFilter uses).
 * Statuses can be shown but disabled (with a note) when selecting them has no
 * effect — e.g. a "Broadcast: skip" status is always excluded anyway.
 */
function StatusMultiSelect({
  options,
  value,
  onChange,
  placeholder,
  disabledNote,
}: {
  options: LeadStatus[];
  value: string[];
  onChange: (names: string[]) => void;
  placeholder: string;
  disabledNote?: (s: LeadStatus) => string | null;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const selected = options.filter((s) => value.includes(s.name));

  const toggle = (name: string) => {
    onChange(value.includes(name) ? value.filter((n) => n !== name) : [...value, name]);
  };

  return (
    <div ref={anchorRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex min-h-[38px] w-full items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-left text-sm transition-colors ${
          open ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-gray-200 hover:border-indigo-300'
        }`}
      >
        <span className="flex flex-1 flex-wrap items-center gap-1">
          {selected.length === 0 ? (
            <span className="text-slate-400">{placeholder}</span>
          ) : (
            selected.map((s) => (
              <span
                key={s.id}
                className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color || '#94a3b8' }} />
                {s.name}
                <span
                  role="button"
                  onClick={(e) => { e.stopPropagation(); toggle(s.name); }}
                  className="text-indigo-400 hover:text-indigo-700"
                >
                  <X className="h-3 w-3" />
                </span>
              </span>
            ))
          )}
        </span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <Floating
        open={open}
        anchorRef={anchorRef}
        onClose={() => setOpen(false)}
        className="rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        style={{ maxHeight: 260, overflowY: 'auto' }}
      >
        {options.length === 0 ? (
          <p className="px-3 py-3 text-center text-xs text-slate-400">No pipeline statuses found.</p>
        ) : (
          options.map((s) => {
            const note = disabledNote?.(s) ?? null;
            const disabled = !!note;
            const checked = value.includes(s.name);
            return (
              <button
                key={s.id}
                type="button"
                disabled={disabled}
                onClick={() => toggle(s.name)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                  disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-indigo-50'
                }`}
              >
                <span
                  className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                    checked ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300 bg-white'
                  }`}
                >
                  {checked && <Check className="h-3 w-3" />}
                </span>
                <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: s.color || '#94a3b8' }} />
                <span className="flex-1 text-slate-700">{s.name}</span>
                {note && <span className="text-[10px] font-medium text-amber-600">{note}</span>}
              </button>
            );
          })
        )}
      </Floating>
    </div>
  );
}

// ── Create broadcast drawer (minimal wizard) ──────────────────────

function CreateBroadcastDrawer({
  accounts,
  onClose,
  onCreated,
}: {
  accounts: WhatsappAccountListItem[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [step, setStep] = useState<'basics' | 'audience'>('basics');
  const [form, setForm] = useState({
    whatsappAccountId: accounts[0]?.id ?? '',
    name: '',
    description: '',
    templateId: '',
  });
  const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [audienceType, setAudienceType] = useState<'all' | 'status'>('all');
  const [leadStatuses, setLeadStatuses] = useState<string[]>([]);
  const [excludeStatuses, setExcludeStatuses] = useState<string[]>([]);
  const [pipelineStatuses, setPipelineStatuses] = useState<LeadStatus[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the tenant's real pipeline statuses so the audience step is a
  // pick-from-list instead of free text. Active statuses only.
  useEffect(() => {
    api.get('/leads/statuses')
      .then(({ data }) => setPipelineStatuses((data as LeadStatus[]).filter((s) => s.isActive !== false)))
      .catch(() => setPipelineStatuses([]));
  }, []);

  // Load templates for chosen account
  useEffect(() => {
    if (!form.whatsappAccountId) return;
    setLoadingTpl(true);
    listTemplates({ whatsappAccountId: form.whatsappAccountId, status: 'APPROVED', limit: 50 })
      .then((res) => setTemplates(res.data))
      .catch(() => setTemplates([]))
      .finally(() => setLoadingTpl(false));
  }, [form.whatsappAccountId]);

  const set = (k: keyof typeof form, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const goNext = () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.templateId) { setError('Select an approved template.'); return; }
    setError(null);
    setStep('audience');
  };

  const submit = async () => {
    if (audienceType === 'status' && leadStatuses.length === 0) {
      setError('Pick at least one lead status to include (or choose "All opted-in contacts").');
      return;
    }
    setSaving(true);
    setError(null);
    const audienceFilter: Record<string, unknown> =
      audienceType === 'all' ? {} : { leadStatuses };
    if (excludeStatuses.length > 0) audienceFilter.excludeStatuses = excludeStatuses;
    try {
      const broadcast = await createBroadcast({
        whatsappAccountId: form.whatsappAccountId,
        name: form.name,
        description: form.description || undefined,
        templateId: form.templateId,
        audienceFilter,
        variableMapping: {},
      });
      toast('success', 'Broadcast draft created');
      onCreated(broadcast.id);
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Failed to create';
      setError(typeof msg === 'string' ? msg : 'Failed to create broadcast');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/30">
      <div className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h3 className="text-sm font-semibold text-slate-800">
            {step === 'basics' ? 'New Broadcast — Basics' : 'New Broadcast — Audience'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 px-5 py-4">
          {step === 'basics' ? (
            <>
              {/* Account */}
              <div>
                <label className="mb-1 block text-xs font-normal text-slate-600">WhatsApp Account</label>
                <SelectField
                  value={form.whatsappAccountId}
                  onChange={(e) => set('whatsappAccountId', e.target.value)}
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                >
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.displayPhoneNumber} {a.verifiedName ? `(${a.verifiedName})` : ''}
                    </option>
                  ))}
                </SelectField>
              </div>

              {/* Name */}
              <div>
                <label className="mb-1 block text-xs font-normal text-slate-600">
                  Broadcast name <span className="text-red-400">*</span>
                </label>
                <input
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                  placeholder="e.g. Q1 Promo Blast"
                  maxLength={200}
                />
              </div>

              {/* Description */}
              <div>
                <label className="mb-1 block text-xs font-normal text-slate-600">
                  Description <span className="text-slate-400">(optional)</span>
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  rows={2}
                  className="w-full resize-none rounded-md border border-gray-200 px-3 py-2 text-sm"
                  maxLength={2000}
                />
              </div>

              {/* Template */}
              <div>
                <label className="mb-1 block text-xs font-normal text-slate-600">
                  Template <span className="text-red-400">*</span>
                </label>
                {loadingTpl ? (
                  <div className="flex items-center gap-2 py-2 text-sm text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading templates…
                  </div>
                ) : templates.length === 0 ? (
                  <p className="text-xs text-amber-600">
                    No APPROVED templates for this account. Create and approve one in the Templates page first.
                  </p>
                ) : (
                  <SelectField
                    value={form.templateId}
                    onChange={(e) => set('templateId', e.target.value)}
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                  >
                    <option value="">Select a template…</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.language}) — {t.category}
                      </option>
                    ))}
                  </SelectField>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Audience type */}
              <div>
                <label className="mb-2 block text-xs font-normal text-slate-600">Audience</label>
                <div className="space-y-2">
                  {[
                    { value: 'all', label: 'All opted-in contacts', desc: 'Send to everyone with WhatsApp opt-in' },
                    { value: 'status', label: 'Filter by lead status', desc: 'Narrow to specific pipeline stages' },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                        audienceType === opt.value
                          ? 'border-indigo-300 bg-indigo-50'
                          : 'border-gray-200 bg-white hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        value={opt.value}
                        checked={audienceType === opt.value}
                        onChange={() => setAudienceType(opt.value as 'all' | 'status')}
                        className="mt-0.5"
                      />
                      <div>
                        <p className="text-sm font-medium text-slate-700">{opt.label}</p>
                        <p className="text-xs text-slate-400">{opt.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {audienceType === 'status' && (
                <div>
                  <label className="mb-1 block text-xs font-normal text-slate-600">
                    Lead statuses to include
                  </label>
                  <StatusMultiSelect
                    options={pipelineStatuses}
                    value={leadStatuses}
                    onChange={setLeadStatuses}
                    placeholder="Pick one or more statuses…"
                    disabledNote={(s) => (s.skipInBroadcasts ? 'always skipped' : null)}
                  />
                  <p className="mt-0.5 text-[11px] text-slate-400">
                    Only leads currently in the selected pipeline stages are messaged.
                  </p>
                </div>
              )}

              <div>
                <label className="mb-1 block text-xs font-normal text-slate-600">
                  Exclude these statuses (optional)
                </label>
                <StatusMultiSelect
                  options={pipelineStatuses}
                  value={excludeStatuses}
                  onChange={setExcludeStatuses}
                  placeholder="None — nothing extra excluded"
                  disabledNote={(s) => (s.skipInBroadcasts ? 'auto-excluded' : null)}
                />
                <p className="mt-0.5 text-[11px] text-slate-400">
                  Leads in these statuses are skipped. Statuses marked "Broadcast: skip" in
                  Settings → Lead Statuses are always excluded automatically.
                </p>
              </div>

              {/* Plain-language summary of who actually gets the message */}
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3 text-xs text-slate-600">
                <p className="mb-1 flex items-center gap-1.5 font-semibold text-indigo-700">
                  <Users className="h-3.5 w-3.5" /> Who will receive this
                </p>
                {audienceType === 'all' ? (
                  <p>Every lead with a WhatsApp opt-in.</p>
                ) : leadStatuses.length === 0 ? (
                  <p className="text-amber-600">Pick at least one status above, or nobody will be messaged.</p>
                ) : (
                  <p>
                    Opted-in leads currently in{' '}
                    <span className="font-medium text-slate-800">{leadStatuses.join(', ')}</span>.
                  </p>
                )}
                {excludeStatuses.length > 0 && (
                  <p className="mt-0.5">
                    Skipping leads in{' '}
                    <span className="font-medium text-slate-800">{excludeStatuses.join(', ')}</span>.
                  </p>
                )}
                <p className="mt-1 text-[11px] text-slate-400">
                  You'll see the exact recipient count on the next screen — tap "Preview audience" before sending.
                </p>
              </div>

              <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-700">
                <p className="font-medium">Compliance filters are always applied</p>
                <p className="mt-0.5">
                  Contacts without WhatsApp opt-in, opted-out contacts, and those on a cooldown
                  period are automatically excluded regardless of your audience selection.
                </p>
              </div>
            </>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-between gap-2 border-t border-gray-100 px-5 py-3">
          {step === 'audience' ? (
            <>
              <button
                onClick={() => setStep('basics')}
                className="rounded-md border border-gray-200 px-4 py-2 text-sm text-slate-600"
              >
                Back
              </button>
              <button
                onClick={submit}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-slate-300"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Create draft
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="rounded-md border border-gray-200 px-4 py-2 text-sm text-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={goNext}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Next →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Broadcast row ─────────────────────────────────────────────────

function BroadcastCard({
  broadcast,
  onClick,
}: {
  broadcast: BroadcastRow;
  onClick: () => void;
}) {
  const pct = progressPct(broadcast);
  const statusClass = STATUS_BADGE[broadcast.status] ?? 'bg-slate-100 text-slate-500';

  return (
    <button
      onClick={onClick}
      className="w-full rounded-xl border border-gray-100 bg-white p-4 text-left shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-800">{broadcast.name}</span>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${statusClass}`}
            >
              {STATUS_ICONS[broadcast.status]}
              {broadcast.status}
            </span>
          </div>
          {broadcast.description && (
            <p className="mt-0.5 line-clamp-1 text-xs text-slate-400">{broadcast.description}</p>
          )}
        </div>
        <div className="flex-shrink-0 text-right">
          <p className="text-xs text-slate-400">
            {formatDate(broadcast.createdAt)}
          </p>
          {broadcast.scheduledFor && (
            <p className="text-[11px] text-blue-600">
              Scheduled {new Date(broadcast.scheduledFor).toLocaleString()}
            </p>
          )}
        </div>
      </div>

      {/* Stats row */}
      {broadcast.totalRecipients > 0 && (
        <div className="mt-3">
          <div className="mb-1 flex items-center justify-between text-[11px] text-slate-500">
            <span>{broadcast.totalRecipients.toLocaleString()} recipients</span>
            <span>{pct}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-indigo-400 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-1.5 flex items-center gap-3 text-[11px]">
            <span className="text-emerald-600">✓ {broadcast.deliveredCount.toLocaleString()} delivered</span>
            <span className="text-blue-600">👁 {broadcast.readCount.toLocaleString()} read</span>
            {broadcast.failedCount > 0 && (
              <span className="text-red-500">✕ {broadcast.failedCount.toLocaleString()} failed</span>
            )}
          </div>
        </div>
      )}
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'RUNNING', label: 'Running' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELED', label: 'Canceled' },
  { value: 'FAILED', label: 'Failed' },
];

export default function WhatsAppBroadcastsPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<WhatsappAccountListItem[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [broadcasts, setBroadcasts] = useState<BroadcastRow[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    listWhatsappAccounts()
      .then((data) => {
        setAccounts(data);
        if (data.length > 0 && !selectedAccount) setSelectedAccount(data[0].id);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listBroadcasts({
        whatsappAccountId: selectedAccount || undefined,
        status: (status as BroadcastStatus) || undefined,
        page,
        limit: 20,
      });
      setBroadcasts(res.data);
      setTotalPages(res.totalPages);
    } catch {
      toast('error', 'Failed to load broadcasts');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, status, page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <DashboardLayout title="WhatsApp Broadcasts">
      <div className="mx-auto max-w-4xl space-y-5 px-4 py-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">Broadcasts</h1>
            <p className="text-sm text-slate-500">
              Send bulk template messages to your opted-in WhatsApp contacts.
            </p>
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <PlusCircle className="h-4 w-4" />
            New broadcast
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          {accounts.length > 1 && (
            <SelectField
              value={selectedAccount}
              onChange={(e) => { setSelectedAccount(e.target.value); setPage(1); }}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm"
            >
              <option value="">All accounts</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.displayPhoneNumber}
                </option>
              ))}
            </SelectField>
          )}
          <SelectField
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="rounded-md border border-gray-200 px-3 py-1.5 text-sm"
          >
            {STATUS_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </SelectField>
        </div>

        {/* Broadcasts */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
          </div>
        ) : broadcasts.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-16 text-center">
            <Megaphone className="mb-3 h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-500">No broadcasts yet</p>
            <p className="mt-1 text-xs text-slate-400">
              Create a new broadcast to send bulk template messages to your contacts.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {broadcasts.map((b) => (
              <BroadcastCard
                key={b.id}
                broadcast={b}
                onClick={() => navigate(`/whatsapp/broadcasts/${b.id}`)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-md border border-gray-200 p-1.5 hover:bg-slate-50 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-slate-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-md border border-gray-200 p-1.5 hover:bg-slate-50 disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {createOpen && (
        <CreateBroadcastDrawer
          accounts={accounts}
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => {
            setCreateOpen(false);
            navigate(`/whatsapp/broadcasts/${id}`);
          }}
        />
      )}
    </DashboardLayout>
  );
}
